import RestEndpointsEnum from "../models/RestEndpointsEnum";
import { buildRestUrl, buildAuthHeadersForFetch } from "./RestService";
import IUserUI from "../models/IUserUI";
import ISupplierModel from "../models/ISupplierModel";
import ITokenSettings from "../models/ITokenSettings";
import IConnection_Token from "../models/IConnection_Token";

interface TokenSettingsApiResponse {
    success: boolean;
    message?: string;
    token?: IConnection_Token;
}

/**
 * Update token settings via REST API
 * Supports partial updates - only the properties provided will be updated
 *
 * @param tokenGuid - The GUID of the token to update
 * @param settings - Partial settings object with only the properties to update
 * @param userModel - The current user model
 * @param supplierModel - The current supplier model
 * @returns Promise with the updated token
 *
 * @example
 * // Update a single setting
 * await TokenService.updateTokenSettings(token.guid, { theme: "DARK" }, userModel, supplierModel);
 *
 * // Update multiple settings at once
 * await TokenService.updateTokenSettings(token.guid, {
 *   theme: "LIGHT",
 *   orders_cols: { vendor: true, catalog: false },
 *   orders_page_size: 50
 * }, userModel, supplierModel);
 */
const updateTokenSettings = async (
    tokenGuid: string,
    settings: Partial<ITokenSettings>,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<TokenSettingsApiResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.TokensUpdateSettings,
        params: { tokenGuid },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ settings }),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(`Failed to update token settings: ${error.message || response.statusText}`);
    }

    return response.json();
};

const TokenService = {
    updateTokenSettings,
};

export default TokenService;
